import { useState } from 'react';
import { observer } from 'mobx-react';
import { Drawer, Form, Input, Typography, Button, Space, message } from 'antd';
import { LinkOutlined } from '@ant-design/icons';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { trackEvent } from '@/lib/telemetry';

const { Text } = Typography;

interface AddLinkProps {
  open: boolean;
  onClose: () => void;
}

// Per project convention (CLAUDE.md "Prefer drawers over modals for most
// interactive flows"), authoring CRUD lives in drawers. Filename kept for
// import-site compat — callers still import {AddLinkModal}.

export const AddLinkModal = observer(function AddLinkModal({ open, onClose }: AddLinkProps) {
  const { knowledgeDetailStore: kb, themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const [form] = Form.useForm<{ url: string; title?: string; description?: string }>();
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(): Promise<void> {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      await kb.addLinkSource(
        values.url.trim(),
        values.title?.trim() || undefined,
        values.description?.trim() || undefined
      );
      trackEvent('Knowledge.LinkAdded', { knowledgeId: kb.knowledgeId ?? -1 });
      message.success('Link added');
      form.resetFields();
      onClose();
    } catch (err: unknown) {
      const isFormError = err && typeof err === 'object' && 'errorFields' in err;
      if (!isFormError) {
        message.error(err instanceof Error ? err.message : 'Failed to add link');
      }
    } finally {
      setSubmitting(false);
    }
  }

  function handleClose() {
    form.resetFields();
    onClose();
  }

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      placement="right"
      width={520}
      destroyOnClose
      closable={false}
      title={null}
      styles={{
        body: { padding: 0, display: 'flex', flexDirection: 'column' },
        header: { display: 'none' },
      }}
    >
      <div
        style={{
          padding: '20px 24px 18px',
          borderBottom: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
          background: isDark
            ? `linear-gradient(135deg, ${colors.ink[700]}33 0%, ${colors.violet[700]}22 100%)`
            : `linear-gradient(135deg, ${colors.ink[50]} 0%, ${colors.violet[50]} 100%)`,
        }}
      >
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <LinkOutlined style={{ fontSize: 18, color: colors.ink[600] }} />
          <Text strong style={{ fontSize: 16 }}>Add a link</Text>
        </div>
        <Text type="secondary" style={{ display: 'block', fontSize: 13, lineHeight: 1.55 }}>
          Authoritative sources scraped on a schedule. Use for guideline pages, FAQs, or anything
          living on the public web that should ground agent answers.
        </Text>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px' }}>
        <Form form={form} layout="vertical" preserve={false} requiredMark="optional">
          <Form.Item
            name="url"
            label="URL"
            rules={[
              { required: true, message: 'A URL is required' },
              { type: 'url', message: 'Enter a valid URL (with https://)' },
            ]}
          >
            <Input placeholder="https://www.cancer.org/treatment/treatments-and-side-effects/..." autoFocus />
          </Form.Item>
          <Form.Item
            name="title"
            label="Title"
            extra="What this link should be called when cited in agent responses."
          >
            <Input placeholder="Optional — defaults to the URL" maxLength={300} />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} maxLength={500} />
          </Form.Item>
        </Form>
      </div>

      <div
        style={{
          padding: '12px 24px',
          borderTop: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        }}
      >
        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
          <Button type="primary" loading={submitting} onClick={handleSubmit}>
            Add link
          </Button>
        </Space>
      </div>
    </Drawer>
  );
});
